import {
  useEffect, useState, useCallback, useContext,
} from 'react';
import { gql, useMutation, useLazyQuery } from '@apollo/client';
import { useLocalStorage } from 'react-use';
import SwitchTestContext from '../../../../contexts/SwitchTestContext';
import fetchGuestSession from '../../../../tools/UserState/fetchGuestSession';
import useUserState from '../../../../tools/UserState/useUserState';

const getSavesListQuery = gql`
query SavesListQuery {
  savesList {
    productId
    kic
    sku
  }
}
`;

export const addToListMutation = gql`
mutation addToListRequest($sku: String, $productId: String!, $kic: String!) {
  addToListRequest(sku: $sku, productId: $productId, kic: $kic) {
    success
    responseData {
      productId
      kic
      sku
    }
    errors {
      message {
          key
          value
      }
      status
    }
  }
}
`;

export const removeFromListMutation = gql`
mutation removeFromListRequest($sku: String, $productId: String!, $kic: String!) {
  removeFromListRequest(sku: $sku, productId: $productId, kic: $kic) {
    success
    responseData {
      productId
      kic
      sku
    }
    errors {
      message {
          key
          value
      }
      status
    }
  }
}
`;

function formatList(rawList) {
  const formattedList = {};
  rawList.forEach((currentItem) => {
    const key = currentItem.productId;
    formattedList[key] = {
      productId: currentItem.productId,
      kic: currentItem.kic,
      sku: currentItem.sku,
    };
  });
  return formattedList;
}

const GET_DEFAULT_MYSAVES_OBJECT = () => ({
  list: {},
  savesCount: 0,
  timestamp: -1,
  timeStamp: GET_NEW_TIMESTAMP(),
});

const GET_NEW_TIMESTAMP = () => new Date().getTime() + 60000;

function getSettings(addToList, removeFromList) {
  return {
    ADDING: {
      mutationToMake: addToList,
      processingState: 'ADDING',
      resetState: 'DEFAULT',
      listDataType: 'addToListRequest',
      analyticsEvent: 'save_for_later',
    },
    REMOVING: {
      mutationToMake: removeFromList,
      processingState: 'REMOVING',
      resetState: 'SAVED',
      listDataType: 'removeFromListRequest',
      analyticsEvent: 'remove_from_saves',
    },
  };
}

export default (productId, kic, sku, store, storeId) => {
  const [buttonState, setButtonState] = useState('DEFAULT');
  const [savesList, setSavesList] = useState({});

  const [getList] = useLazyQuery(getSavesListQuery, {});
  const [addToList] = useMutation(addToListMutation);
  const [removeFromList] = useMutation(removeFromListMutation);

  const [savesListLocalStorage, setSavesListLocalStorage] = useLocalStorage('savesList', GET_DEFAULT_MYSAVES_OBJECT());
  const isCurrentProductSaved = typeof savesList[productId] !== 'undefined';
  const SwitchTestState = useContext(SwitchTestContext);
  const pdpGuestSessionMfeFlag = SwitchTestState['has-pdp-guest-session-fetch-mfe'];
  const { isGenericUser } = useUserState(storeId);

  const guestSessionFail = useCallback(() => {
    setButtonState(getSettings().ADDING.resetState);
  }, []);

  useEffect(() => {
    getList().then(({ data }) => {
      const formattedList = formatList(data.savesList);
      setSavesList(formattedList);
      setSavesListLocalStorage(formattedList);
    }).catch(() => {
      setSavesList({});
    });
  }, [getList, setSavesListLocalStorage]);

  useEffect(() => {
    setButtonState(isCurrentProductSaved ? 'SAVED' : 'DEFAULT');
  }, [isCurrentProductSaved]);

  useEffect(() => {
    window.addEventListener('crs:guestSessionFail', guestSessionFail);

    return () => {
      window.removeEventListener('crs:guestSessionFail', guestSessionFail);
    };
  }, [
    guestSessionFail,
  ]);

  async function handleSavesClick() {
    const settings = getSettings(addToList, removeFromList);
    let setting = settings.ADDING;

    if (isCurrentProductSaved) {
      setting = settings.REMOVING;
    }

    setButtonState(setting.processingState);

    if (pdpGuestSessionMfeFlag) {
      try {
        if (isGenericUser()) await fetchGuestSession(store);
        executeMutation(setting);
      } catch (err) {
        setButtonState(setting.resetState);
      }
    } else {
      const guestSessionSuccess = function guestSessionSuccess() {
        window.removeEventListener('crs:guestSessionSuccess', guestSessionSuccess);
        executeMutation(setting);
      };
      window.addEventListener('crs:guestSessionSuccess', guestSessionSuccess);
      window.dispatchEvent(new CustomEvent('mfe:callGuestSession'));
    }
  }

  function executeMutation(setting) {
    setting.mutationToMake({
      variables: { sku, productId, kic },
      onCompleted: (data) => {
        handleMutationComplete(data[setting.listDataType].responseData);
        window.digitalData.trigger(
          `analytics.${setting.analyticsEvent}`,
          {
            product_id: productId || 'null',
            short_sku: sku || 'null',
            key_item_color: kic || 'null',
          },
        );
      },
      onError: () => {
        setButtonState(setting.resetState);
      },
    });
  }

  const handleMutationComplete = (data) => {
    const formattedList = formatList(data);
    setSavesList(formattedList);
    if (!isCurrentProductSaved) {
      const saveEvent = new CustomEvent('crs:mySaveSuccess');
      window.dispatchEvent(saveEvent);
    }
    setSavesListLocalStorage({
      ...savesListLocalStorage,
      list: formattedList,
      savesCount: data.length,
      timeStamp: GET_NEW_TIMESTAMP(),
    });
  };

  return {
    buttonState,
    handleSavesClick,
  };
};
